import React, { useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { COLORS, FONT } from "../../constants";

const SignaturePad = ({ saved, onSave, onClear }) => {
  const sigRef = useRef(null);
  const containerRef = useRef(null);

  const prevHeight = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return;
      const { width, height } = entry.contentRect;
      const newHeight = Math.round(height);
      if (prevHeight.current !== null && prevHeight.current !== newHeight) {
        sigRef.current?.clear();
        onClear();
      }
      prevHeight.current = newHeight;
      canvas.width = Math.round(width);
      canvas.height = newHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onClear]);

  const handleSave = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      onSave(sigRef.current.toDataURL("image/png"));
    }
  };

  const handleClear = () => {
    sigRef.current?.clear();
    onClear();
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: FONT.size.xs, fontWeight: 600, color: COLORS.darkText, letterSpacing: 0.5, mb: 0.75 }}>
        SIGNATURE
      </Typography>

      <Box
        ref={containerRef}
        sx={{
          border: `1px solid ${saved ? COLORS.themeRed : "#d0d0d0"}`,
          borderRadius: "4px",
          backgroundColor: COLORS.white,
          overflow: "hidden",
          lineHeight: 0,
          transition: "border-color 0.2s",
          resize: "vertical",
          height: "140px",
          minHeight: "100px",
        }}
      >
        {saved ? (
          <Box
            component="img"
            src={saved}
            alt="Saved signature"
            sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <SignatureCanvas
            ref={sigRef}
            penColor="#1a1a1a"
            backgroundColor="rgb(255,255,255)"
            canvasProps={{ style: { width: "100%", height: "100%", display: "block", cursor: "crosshair" } }}
          />
        )}
      </Box>

      {!saved && (
        <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.lightText, mt: 0.5, mb: 1.5 }}>
          Sign in the box above — drag the corner to resize
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: saved ? 1 : 0 }}>
        <Button
          onClick={handleClear}
          sx={{
            fontSize: FONT.size.xs,
            fontWeight: 500,
            textTransform: "none",
            color: COLORS.darkText,
            border: "1px solid #d0d0d0",
            borderRadius: "2px",
            px: 2.5,
            py: 0.75,
            boxShadow: "none",
            "&:hover": { borderColor: COLORS.themeBlack, backgroundColor: "transparent" },
          }}
        >
          Clear
        </Button>
        <Button
          onClick={handleSave}
          disabled={!!saved}
          sx={{
            fontSize: FONT.size.xs,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: COLORS.themeBlack,
            color: COLORS.white,
            borderRadius: "2px",
            px: 2.5,
            py: 0.75,
            boxShadow: "none",
            "&:hover": { backgroundColor: COLORS.themeBlack, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#cccccc", color: COLORS.white },
          }}
        >
          Save Signature
        </Button>

        {saved && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
            <Typography sx={{ fontSize: FONT.size.xs, color: "#2e7d32", fontWeight: 500 }}>
              Signature saved
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SignaturePad;
